import { create } from "zustand";
import { notificationsConfig } from "../config/notifications.config.js";
import {
  findNotificationInList,
  markNotificationAsReadVm,
  replaceNotificationInList,
} from "../lib/apply-notification-read-state.js";
import { toNotificationVmList } from "../lib/to-notification-vm.js";
import notificationService from "../services/notification-service.js";

/**
 * Store do inbox de notificações (server state em Zustand — exceção §3.8
 * pedida pelo usuário: cobre GET/list+polling e mark-as-read write).
 * Única fonte de verdade da lista/status; sem persist.
 *
 * @typedef {Object} NotificationsStoreState
 * @property {Object[]} notifications
 * @property {boolean} isLoading
 * @property {boolean} isMarkingRead
 * @property {string|null} error
 * @property {(params?: { limit?: number|string }, requestOptions?: RequestInit) => Promise<*>} fetchNotifications
 * @property {(notificationId: string) => Object|null} applyMarkAsReadOptimistic
 * @property {(notificationId: string, snapshot: Object|null) => void} revertNotification
 * @property {(message?: string|null) => void} clearError
 */

export const useNotificationsStore = create((set, get) => ({
  notifications: [],
  isLoading: false,
  isMarkingRead: false,
  error: null,

  /**
   * Lista notificações via notificationService.list e mapeia DTO→VM.
   * @param {{ limit?: number|string }} [params]
   * @param {RequestInit} [requestOptions]
   */
  fetchNotifications: async (params = {}, requestOptions) => {
    set({ isLoading: true, error: null });

    try {
      const response = await notificationService.list(
        {
          limit: params?.limit ?? notificationsConfig.defaultLimit,
          ...(typeof params?.hideNonEssentialRead === "boolean"
            ? { hideNonEssentialRead: params.hideNonEssentialRead }
            : {}),
        },
        requestOptions,
      );

      if (!response?.success) {
        set({
          isLoading: false,
          error: response?.message || "Erro ao carregar notificações",
        });
        return response;
      }

      const notifications = toNotificationVmList(
        response.data?.notifications,
      );

      set({
        notifications,
        isLoading: false,
        error: null,
      });

      return {
        ...response,
        data: { notifications },
      };
    } catch (err) {
      const message = err?.message || "Erro ao carregar notificações";
      set({ isLoading: false, error: message });
      return {
        success: false,
        message,
        status: 500,
        code: "UNKNOWN_ERROR",
        error: err,
      };
    }
  },

  /**
   * Aplica status=read otimista e devolve snapshot para rollback.
   * @param {string} notificationId
   * @returns {Object|null} snapshot pré-otimista
   */
  applyMarkAsReadOptimistic: (notificationId) => {
    const id =
      typeof notificationId === "string" ? notificationId.trim() : "";
    if (!id) return null;

    const { notifications } = get();
    const snapshot = findNotificationInList(notifications, id);
    if (!snapshot) return null;

    const nextItem = markNotificationAsReadVm(snapshot);
    set({
      notifications: replaceNotificationInList(notifications, id, nextItem),
      isMarkingRead: true,
      error: null,
    });

    return snapshot;
  },

  /**
   * Restaura item a partir do snapshot (rollback pós-falha do POST).
   * @param {string} notificationId
   * @param {Object|null} snapshot
   */
  revertNotification: (notificationId, snapshot) => {
    const id =
      typeof notificationId === "string" ? notificationId.trim() : "";
    if (!id || !snapshot) {
      set({ isMarkingRead: false });
      return;
    }

    const { notifications } = get();
    set({
      notifications: replaceNotificationInList(notifications, id, snapshot),
      isMarkingRead: false,
    });
  },

  /**
   * @param {string|null} [message]
   */
  clearError: (message = null) => {
    set({ error: message });
  },

  /**
   * Finaliza flag de mutation de leitura (sucesso).
   */
  finishMarkAsRead: () => {
    set({ isMarkingRead: false });
  },
}));
